<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import LocalCheckoutSheet from '@/components/LocalCheckoutSheet.vue'
import UigIcon from '@/components/UigIcon.vue'
import { useModal } from '@/composables/useModal'
import { legalLinks } from '@/data/legal'
import { capture } from '@/services/analytics'
import { api, ApiError } from '@/services/api'
import { metaCookies, trackCheckoutReturn, trackCheckoutStarted } from '@/services/attribution'
import {
  formatLocalized, localizedPrice, openLocalizedCheckout, type LocalizedCheckout, type LocalizedPrice,
} from '@/services/localizedCheckout'
import { onboardingLog } from '@/services/onboardingLog'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useOnboardingStore } from '@/stores/onboarding'
import { useWelcomeBonusStore } from '@/stores/welcomeBonus'
import '@/styles/welcome-bonus.css'

const app = useAppStore()
const auth = useAuthStore()
const onboarding = useOnboardingStore()
const bonus = useWelcomeBonusStore()
const route = useRoute()
const router = useRouter()
const opened = ref(false)
const busy = ref(false)
const returning = ref(false)
const notice = ref('')
const successful = ref(false)
const productPage = computed(() => ['home', 'studio', 'profile'].includes(String(route.name)))
const available = computed(() => productPage.value && app.publicConfig.welcome_bonus_enabled && auth.isAuthenticated)
const showBanner = computed(() => available.value && bonus.active)
const sheet = ref<LocalizedCheckout>()
const localPrice = ref<LocalizedPrice | null>(null)
const publishableKey = computed(() => app.publicConfig.stripe_publishable_key || '')
const price = computed(() => localPrice.value
  ? formatLocalized(localPrice.value, localPrice.value.dueToday)
  : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: bonus.offer?.currency ?? 'USD',
  }).format(bonus.offer?.price ?? 2.99))
const days = ['Today', 'Day 2', 'Day 3']
let timer: number | undefined
let ticks = 0
let returnInFlight = false
let cancelTracked = false
let prepared: Promise<LocalizedCheckout | null> | undefined
let preparedVersion = 0

function resetLocalCheckout() {
  preparedVersion++
  prepared = undefined
  localPrice.value = null
  sheet.value = undefined
}

function adoptCheckout(clientSecret: string, sessionId: string) {
  const version = preparedVersion
  prepared = openLocalizedCheckout(publishableKey.value, clientSecret, sessionId).then((checkout) => {
    if (version !== preparedVersion) return null
    localPrice.value = localizedPrice(checkout.actions.getSession())
    checkout.sdk.on('change', (session) => {
      if (version === preparedVersion) localPrice.value = localizedPrice(session)
    })
    return checkout
  })
  return prepared
}

async function prepareLocalPrice() {
  if (prepared || !publishableKey.value || !bonus.active) return
  const version = preparedVersion
  // Preview creates the single offer-bound session early, so the banner can show Stripe's local price.
  prepared = api.checkout('welcome_bonus', crypto.randomUUID(), metaCookies(), { uiMode: 'elements', preview: true })
    .then((response) => version === preparedVersion && response.client_secret && response.checkout_session_id
      ? adoptCheckout(response.client_secret, response.checkout_session_id)
      : null)
  try {
    await prepared
  } catch {
    if (version === preparedVersion) prepared = undefined
    onboardingLog('warn', '[UNI-149] welcome_bonus_local_pricing_unavailable')
  }
}

function trackPaywall(event: string, properties: Record<string, unknown> = {}) {
  capture(event, { paywall: 'welcome_bonus', placement: 'app', product_id: 'welcome_bonus', ...properties })
}

function close() {
  if (busy.value || sheet.value) return
  if (opened.value && !successful.value && !returning.value) trackPaywall('paywall_closed')
  opened.value = false
  successful.value = false
  notice.value = ''
}

function openOffer(event: MouseEvent) {
  // Safari does not focus buttons on pointer click; preserve a useful return focus for the dialog.
  ;(event.currentTarget as HTMLButtonElement).focus({ preventScroll: true })
  opened.value = true
  notice.value = ''
  trackPaywall('paywall_shown')
}

useModal(opened, close)

async function refresh() {
  const previousCredits = bonus.offer?.credits_granted
  await bonus.refresh()
  if (bonus.offer?.status === 'purchased' && previousCredits !== bonus.offer.credits_granted) await auth.refresh()
}

async function buy() {
  if (busy.value || !bonus.active || !auth.requireSignIn()) return
  busy.value = true
  notice.value = ''
  const owner = auth.identity?.user_id
  try {
    // Server rechecks eligibility and uses a single account-bound Checkout session.
    const local = Boolean(publishableKey.value)
    const checkout = await api.checkout('welcome_bonus', crypto.randomUUID(), metaCookies(), local ? { uiMode: 'elements' } : {})
    if (auth.identity?.user_id !== owner) return
    trackCheckoutStarted('welcome_bonus', checkout)
    onboardingLog('debug', '[FIX:UNI-116] welcome_bonus_checkout_started')
    if (local && checkout.client_secret && checkout.checkout_session_id) {
      const ready = await prepared?.catch(() => null)
      sheet.value = ready?.sessionId === checkout.checkout_session_id
        ? ready
        : await adoptCheckout(checkout.client_secret, checkout.checkout_session_id) ?? undefined
      return
    }
    if (!checkout.checkout_url) throw new Error('Checkout is unavailable.')
    window.location.assign(checkout.checkout_url)
  } catch (caught) {
    if (auth.identity?.user_id !== owner) return
    trackPaywall('paywall_payment_error', { reason: 'checkout_unavailable', error_status: caught instanceof ApiError ? caught.status : null })
    notice.value = caught instanceof ApiError ? caught.message : 'Checkout could not be started. Please try again.'
    onboardingLog('warn', '[FIX:UNI-116] welcome_bonus_checkout_failed', { status: caught instanceof ApiError ? caught.status : 'network' })
    await bonus.refresh()
  } finally {
    busy.value = false
  }
}

function closeSheet() {
  sheet.value = undefined
  trackPaywall('paywall_payment_cancelled')
}

async function sheetPaid(sessionId: string) {
  sheet.value = undefined
  await router.replace({ path: route.path, query: { ...route.query, welcome_bonus: 'success', session_id: sessionId } })
}

async function checkReturn() {
  if (returnInFlight || !auth.ready || !available.value || route.query.welcome_bonus !== 'success') return
  const sessionId = typeof route.query.session_id === 'string' ? route.query.session_id : ''
  const owner = auth.identity?.user_id
  returnInFlight = true
  returning.value = true
  opened.value = true
  notice.value = ''
  try {
    if (!sessionId) throw new Error('Missing Checkout Session')
    trackCheckoutReturn(sessionId)
    // Local Stripe test mode can reconcile without a webhook tunnel. Production waits for the webhook.
    if (app.publicConfig.checkout_confirmation_enabled) await api.confirmCheckout(sessionId)
    await bonus.refresh()
    if (auth.identity?.user_id !== owner) return
    if (bonus.offer?.status === 'purchased') {
      await auth.refresh()
      if (auth.identity?.user_id !== owner) return
      successful.value = true
      if (!onboarding.completed) onboarding.complete('confirmed_checkout')
      const query = { ...route.query }
      delete query.welcome_bonus
      delete query.session_id
      await router.replace({ path: route.path, query })
    } else if (bonus.offer?.status === 'revoked') {
      notice.value = 'This Welcome Bonus payment was refunded or disputed. Remaining rewards have been stopped.'
    } else {
      notice.value = 'Your payment is being confirmed. Your credits will appear automatically.'
    }
  } catch {
    if (auth.identity?.user_id === owner) notice.value = 'We could not confirm your payment yet. Check again in a moment.'
  } finally {
    returning.value = false
    returnInFlight = false
  }
}

watch(() => auth.identity?.user_id, () => {
  bonus.reset()
  resetLocalCheckout()
  opened.value = false
  successful.value = false
  notice.value = ''
})

watch([available, () => auth.ready, () => auth.identity?.user_id, () => route.query.welcome_bonus], async () => {
  if (!available.value || !auth.ready || !auth.identity) return
  bonus.tick()
  await bonus.enter(auth.identity.user_id)
  await checkReturn()
  if (route.query.welcome_bonus === 'cancelled' && bonus.active) {
    if (!cancelTracked) {
      trackPaywall('paywall_payment_cancelled')
      trackPaywall('paywall_shown')
    }
    cancelTracked = true
    notice.value = 'Checkout was cancelled. You have not been charged.'
    opened.value = true
  }
}, { immediate: true })

watch(productPage, (visible) => { if (!visible) close() })
watch([showBanner, publishableKey], ([visible]) => { if (visible) void prepareLocalPrice() }, { immediate: true })
watch(() => bonus.active, (active, wasActive) => {
  if (!active && wasActive && !busy.value && !returning.value && !successful.value && route.query.welcome_bonus !== 'success') {
    opened.value = false
  }
})

function onVisible() {
  bonus.tick()
  updateTimer()
  if (document.hidden || !available.value) return
  void refresh()
  void checkReturn()
}

function updateTimer() {
  window.clearInterval(timer)
  const pendingGrants = bonus.offer?.status === 'purchased' && bonus.offer.next_grant_at
  if (document.hidden || !available.value || (!bonus.active && !pendingGrants && route.query.welcome_bonus !== 'success')) return
  timer = window.setInterval(() => {
    bonus.tick()
    ticks++
    if (document.hidden || !available.value || ticks % 15 !== 0) return
    const pendingGrants = bonus.offer?.status === 'purchased' && bonus.offer.next_grant_at
    if (bonus.active || pendingGrants) void refresh()
    void checkReturn()
  }, 1000)
}

watch([available, () => bonus.active, () => bonus.offer?.next_grant_at, () => route.query.welcome_bonus], updateTimer)

onMounted(() => {
  updateTimer()
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('focus', onVisible)
})
onBeforeUnmount(() => {
  window.clearInterval(timer)
  document.removeEventListener('visibilitychange', onVisible)
  window.removeEventListener('focus', onVisible)
})
</script>

<template>
  <aside
    v-if="productPage && auth.ready && !auth.isAuthenticated && route.query.welcome_bonus === 'success'"
    class="welcome-return"
    role="status"
  >
    <p>Sign in with the purchasing account to check your Welcome Bonus.</p>
    <button
      type="button"
      class="secondary-button"
      @click="auth.openSignIn()"
    >
      Sign in
    </button>
  </aside>
  <button
    v-if="showBanner"
    type="button"
    class="welcome-banner"
    aria-haspopup="dialog"
    :aria-expanded="opened"
    :aria-label="`Welcome Bonus: 300 credits for ${price}. View offer`"
    @click="openOffer"
  >
    <span class="welcome-bolt welcome-banner__icon"><UigIcon
      name="bolt_fill"
      :size="32"
    /></span>
    <span class="welcome-banner__copy">
      <strong>Welcome Bonus</strong>
      <span class="welcome-banner__details">
        <b class="welcome-banner__price">{{ price }}</b>
        <span><UigIcon
          name="bolt_circle_fill"
          :size="17"
        /> {{ bonus.offer?.total_credits ?? 300 }}</span>
      </span>
    </span>
    <span
      class="welcome-countdown"
      aria-label="Time remaining"
    >{{ bonus.countdown }}</span>
  </button>

  <Teleport to="body">
    <Transition name="welcome-sheet">
      <div
        v-if="opened && !sheet"
        class="welcome-backdrop"
        @click.self="close"
      >
        <section
          class="welcome-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-title"
          aria-describedby="welcome-description"
          :aria-busy="busy || returning"
        >
          <button
            class="welcome-close"
            type="button"
            aria-label="Close Welcome Bonus"
            :disabled="busy"
            @click="close"
          >
            <UigIcon
              name="xmark"
              :size="17"
            />
          </button>
          <p class="welcome-eyebrow">
            <span class="welcome-bolt"><UigIcon
              name="bolt_fill"
              :size="21"
            /></span> Exclusive for Welcome Bonus
          </p>
          <template v-if="successful">
            <h2 id="welcome-title">
              {{ bonus.offer?.credits_granted ?? 100 }} Credits Added!
            </h2>
            <p
              id="welcome-description"
              class="welcome-schedule"
            >
              {{ bonus.offer?.next_grant_at ? 'Your next 100 credits will arrive automatically, 24 hours after the previous reward.' : 'All 300 credits from your Welcome Bonus have been added.' }}
            </p>
            <button
              class="welcome-claim"
              type="button"
              @click="close"
            >
              Start creating
            </button>
          </template>
          <template v-else>
            <h2 id="welcome-title">
              Luxury rewards for the first <span>3 days</span>
            </h2>
            <div
              class="welcome-days"
              aria-label="300 credits over three days"
            >
              <div
                v-for="day in days"
                :key="day"
                class="welcome-day"
              >
                <strong>{{ day }}</strong>
                <span>{{ bonus.offer?.daily_credits ?? 100 }} <UigIcon
                  name="bolt_circle_fill"
                  :size="28"
                /></span>
              </div>
            </div>
            <p
              id="welcome-description"
              class="welcome-schedule"
            >
              100 credits now, 100 in 24 hours, and 100 in 48 hours. Added automatically.
            </p>
            <p
              v-if="bonus.active"
              class="welcome-deadline"
            >
              Offer ends in <span>{{ bonus.countdown }}</span>
            </p>
            <button
              v-if="route.query.welcome_bonus === 'success'"
              class="welcome-claim"
              type="button"
              :disabled="returning"
              @click="checkReturn"
            >
              {{ returning ? 'Confirming your payment…' : 'Check payment status' }}
            </button>
            <button
              v-else
              class="welcome-claim"
              type="button"
              :disabled="busy || !bonus.active"
              @click="buy"
            >
              {{ busy ? 'Opening checkout…' : bonus.active ? `Claim your offer · ${price}` : 'Offer ended' }}
            </button>
            <p class="welcome-disclaimer">
              This is an optional add-on available for an additional fee. This purchase is not required, your subscription does not depend on it.
            </p>
          </template>
          <p
            v-if="notice || bonus.error"
            class="welcome-notice"
            role="status"
          >
            {{ notice || bonus.error }}
          </p>
          <nav
            class="welcome-legal"
            aria-label="Welcome Bonus information"
          >
            <a
              :href="legalLinks.terms"
              target="_blank"
              rel="noopener"
            >Terms</a>
            <button
              type="button"
              :disabled="busy || bonus.loading"
              @click="refresh().then(() => { notice = bonus.offer?.status === 'purchased' ? 'Your Welcome Bonus purchase is restored.' : 'No Welcome Bonus purchase found for this account.' })"
            >
              Restore
            </button>
            <a
              :href="legalLinks.privacy"
              target="_blank"
              rel="noopener"
            >Privacy</a>
          </nav>
        </section>
      </div>
    </Transition>
    <LocalCheckoutSheet
      v-if="sheet"
      :key="sheet.sessionId"
      :checkout="sheet"
      :price="localPrice"
      title="Welcome Bonus"
      :interval="null"
      @close="closeSheet"
      @paid="sheetPaid"
    />
  </Teleport>
</template>
